import { Suspense, useMemo, useState, useEffect, ReactElement } from "react";
import { BaseEntryResultType, FilterValues, KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { fetchKmsData } from "@kaltura/mediaspace-shared-utils";
import { ErrorBoundary } from "@kaltura/mediaspace-shared-ui";
import { SearchResultsDisplayComponentProps } from "./types";

export interface SearchResultsContainerProps<SearchResultT, PropsT = never> {
    searchUrl: string;
    filterValues?: FilterValues;
    onEntryShowMore: (searchResult: BaseEntryResultType, index?: number) => void;

    /**
     * does the component have full page width or explore-bar open
     */
    isWideLayout: boolean;
    /**
     * search-in label (20 results found in `Details`)
     */
    searchInLabel?: string;

    /**
     * component that displays actual results
     */
    searchResultsDisplayComponent: (props: SearchResultsDisplayComponentProps<SearchResultT> & PropsT) => ReactElement;

    /**
     * component to display while loading
     */
    searchResultsSkeletonComponent: () => ReactElement;

    /**
     * bulk button actions list
     */
    bulkButtonActions?: KmsTypeComponent[];

    /**
     * bulk menu actions list
     */
    bulkMenuActions?: KmsTypeComponent[];

    displayComponentProps?: PropsT;
}

/**
 * Global Search Results Container - fetch search results from kms
 */
export function SearchResultsContainer<SearchResultT, PropsT>(
    props: SearchResultsContainerProps<SearchResultT, PropsT>
) {
    const {
        searchUrl,
        filterValues,
        onEntryShowMore,
        isWideLayout,
        searchInLabel,
        searchResultsDisplayComponent: SearchResultsDisplayComponent,
        searchResultsSkeletonComponent: SearchResultsSkeletonComponent,
        bulkButtonActions,
        bulkMenuActions,
        displayComponentProps,
    } = props;
    const [page, setPage] = useState(1);
    const [invalidateCount, setInvalidateCount] = useState(0);

    // parse url params
    const dummyUrl = new URL("https://www.kaltura.com"); // just a dummy for parsing
    page > 1 && dummyUrl.searchParams.append("page", String(page));
    const url = `${searchUrl}${dummyUrl.search}`;

    /**
     * do the given filters represent a search.
     * a search is a combination of filter values, other than the 'sortBy' filter
     * @param filterValues
     */
    const isSearch = (filterValues?: FilterValues) => {
        if (!filterValues) {
            return false;
        }

        for (const param in filterValues) {
            if (
                param !== "sortBy" // don't care about sort
                && filterValues[param]
                && filterValues[param].length // either non-empty array or non-empty string
                && filterValues[param][0].length
            ) {
                // first element is not empty (for strings this is true anyway)
                return true;
            }
        }
        return false;
    };

    // fetch data from kms
    const fetchModuleData = useMemo(() => {
        return fetchKmsData<{ data: SearchResultT }>(url, filterValues, true);
    }, [filterValues, url, invalidateCount]);

    // handle page change
    const handlePageChange = (page: number) => {
        setPage(page);
    };

    // reset page on param change
    useEffect(() => {
        setPage(1);
    }, [filterValues]);

    const onInvalidate = () => {
        // child components can trigger invalidateCount state update,
        // which will invalidate the fetchModuleData result
        setInvalidateCount((prev) => prev + 1);
    };

    // render with Suspense
    return (
        <ErrorBoundary fallback={<SearchResultsSkeletonComponent />}>
            <Suspense fallback={<SearchResultsSkeletonComponent />}>
                {/* @ts-expect-error (dynamic prop types for displayComponentProps) */}
                <SearchResultsDisplayComponent
                    isSearch={isSearch(filterValues)}
                    isTextSearch={!!filterValues?.keyword}
                    fetchModuleData={fetchModuleData}
                    page={page}
                    onPageChange={handlePageChange}
                    onEntryShowMore={onEntryShowMore}
                    isWideLayout={isWideLayout}
                    searchInLabel={searchInLabel}
                    bulkButtonActions={bulkButtonActions}
                    bulkMenuActions={bulkMenuActions}
                    onInvalidate={onInvalidate}
                    {...displayComponentProps}
                />
            </Suspense>
        </ErrorBoundary>
    );
}

export default SearchResultsContainer;
